import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/base/button";
import { useStateMachine } from "@/core/arch/state_machine";
import {
  ActivationBonusBackfillBloc,
  splitEmails,
  type BackfillCandidate,
  type BackfillDisposition,
  type ResolvedBackfill,
} from "@/application/tools/activation_bonus_backfill_bloc";
import { activationBonusBackfillRepository } from "@/application/tools/activation_bonus_backfill_repository";
import { JobStatusBadge } from "./JobStatusBadge";
import { ToolSheetShell } from "./ToolSheetShell";
import type { ToolSheetProps } from "../tools";

const DISPOSITION_LABELS: Record<BackfillDisposition, string> = {
  will_pay: "Will pay",
  will_revive: "Will reopen & pay",
  already_paid: "Already paid",
  paid_externally: "Paid manually in Stripe",
  wrong_stage: "Not eligible",
  no_merchant: "No merchant found",
  no_business: "No business",
  no_destination: "No Stripe account",
  duplicate: "Duplicate",
  lookup_failed: "Lookup failed",
};

const COUNTER_LABELS: Record<string, string> = {
  paid: "Paid",
  revived: "Reopened",
  alreadyPaid: "Already paid",
  paidExternally: "Paid manually",
  wrongStage: "Not eligible",
  noMerchant: "No merchant",
  noBusiness: "No business",
  noDestination: "No Stripe account",
  lookupFailed: "Lookup failed",
  failed: "Failed",
};

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function dispositionClass(d: BackfillDisposition): string {
  if (d === "will_pay") return "text-emerald-700 bg-emerald-50";
  // Distinguished from `will_pay`: this one reverses a closed track, so it
  // should not read as routine in a list the operator is approving.
  if (d === "will_revive") return "text-violet-700 bg-violet-50";
  if (d === "lookup_failed") return "text-amber-700 bg-amber-50";
  if (d === "already_paid" || d === "paid_externally")
    return "text-muted-foreground bg-accent";
  return "text-subtle-foreground bg-muted";
}

function CandidateTable({ candidates }: { candidates: BackfillCandidate[] }) {
  if (candidates.length === 0) return null;

  // Payable first: those are what the operator is approving. Revives lead —
  // they are the rows that reverse a closed track.
  const ordered = [...candidates].sort((a, b) => {
    const rank = (c: BackfillCandidate) =>
      c.disposition === "will_revive"
        ? 0
        : c.disposition === "will_pay"
          ? 1
          : 2;
    return rank(a) - rank(b) || a.email.localeCompare(b.email);
  });

  return (
    <div className="border-border mt-4 max-h-80 overflow-auto rounded-md border">
      <table className="w-full text-left text-xs">
        <thead className="bg-muted text-muted-foreground sticky top-0">
          <tr>
            <th className="px-3 py-2 font-medium">Email</th>
            <th className="px-3 py-2 font-medium">Business</th>
            <th className="px-3 py-2 font-medium">Stage</th>
            <th className="px-3 py-2 font-medium">Outcome</th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {ordered.map((c) => (
            <tr key={c.email}>
              <td className="text-foreground px-3 py-2">{c.email}</td>
              <td className="text-muted-foreground px-3 py-2">
                {c.businessName ?? "—"}
              </td>
              <td className="text-muted-foreground px-3 py-2">
                {c.stage ?? "—"}
              </td>
              <td className="px-3 py-2">
                <span
                  className={`inline-block rounded px-1.5 py-0.5 ${dispositionClass(c.disposition)}`}
                  title={c.detail ?? undefined}
                >
                  {DISPOSITION_LABELS[c.disposition]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResolvedSummary({ resolved }: { resolved: ResolvedBackfill }) {
  const skipped = resolved.candidates.length - resolved.payableCount;
  const unresolvable = resolved.candidates.filter(
    (c) => c.disposition === "lookup_failed",
  ).length;

  return (
    <>
      <div className="border-border bg-muted mt-4 rounded-md border p-3">
        <p className="text-foreground text-sm">
          <span className="font-semibold">{resolved.payableCount}</span>{" "}
          merchant{resolved.payableCount === 1 ? "" : "s"} will receive{" "}
          <span className="font-semibold">
            {formatUsd(resolved.totalAmountCents)}
          </span>{" "}
          in total.
        </p>
        <p className="text-muted-foreground mt-1 text-xs">
          {skipped} of {resolved.candidates.length} skipped
          {resolved.duplicates.length > 0 &&
            ` · ${resolved.duplicates.length} duplicate${resolved.duplicates.length === 1 ? "" : "s"} collapsed`}
        </p>
      </div>

      {resolved.reviveCount > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-violet-200 bg-violet-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-violet-500" />
          <p className="text-xs text-violet-800">
            {resolved.reviveCount} of those{" "}
            {resolved.reviveCount === 1 ? "has a" : "have a"} closed track that
            will be reopened first. Expired and failed tracks are terminal to
            every automatic path, so this is a deliberate reversal — included in
            the total above.
          </p>
        </div>
      )}

      {unresolvable > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800">
            {unresolvable} merchant{unresolvable === 1 ? "" : "s"} could not be
            checked against Stripe and will be skipped. Re-run the resolve step
            to retry — paying without that check risks a duplicate transfer.
          </p>
        </div>
      )}

      <CandidateTable candidates={resolved.candidates} />
    </>
  );
}

function parseCounters(raw: string | null): Record<string, number> | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, number>;
  } catch {
    return null;
  }
}

function CounterGrid({ raw }: { raw: string | null }) {
  const counters = parseCounters(raw);
  if (!counters) return null;

  const entries = Object.entries(counters).filter(([, value]) => value > 0);
  if (entries.length === 0) return null;

  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="border-border bg-muted rounded-md border px-3 py-2"
        >
          <p className="text-muted-foreground text-xs">
            {/* eslint-disable-next-line security/detect-object-injection -- key comes from the runner's own counter map, and an unmapped key falls back to itself */}
            {COUNTER_LABELS[key] ?? key}
          </p>
          <p className="text-foreground mt-0.5 text-lg font-semibold">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

export function ActivationBonusBackfillSheet({ onClose }: ToolSheetProps) {
  const [state, bloc] = useStateMachine(
    () => new ActivationBonusBackfillBloc(activationBonusBackfillRepository),
  );

  useEffect(() => {
    bloc.resumeIfRunning();
  }, [bloc]);

  const { phase, emailsInput, reviveClosedTracks } = state;
  const job =
    phase.kind === "watching" || phase.kind === "cancelling" ? phase.job : null;

  const isRunning = job?.status === "running" || job?.status === "pending";
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  const isTerminal =
    job?.status === "complete" ||
    job?.status === "failed" ||
    job?.status === "cancelled";

  const emailCount = splitEmails(emailsInput).length;

  return (
    <ToolSheetShell
      title="Activation Bonus Backfill"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          {phase.kind === "composing" && (
            <Button
              onClick={() => bloc.resolve()}
              disabled={emailCount === 0}
              className="flex-1"
            >
              Resolve{" "}
              {emailCount > 0
                ? `${emailCount} email${emailCount === 1 ? "" : "s"}`
                : ""}
            </Button>
          )}
          {phase.kind === "resolving" && (
            <Button disabled className="flex-1">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Resolving…
            </Button>
          )}
          {phase.kind === "resolved" && (
            <>
              <Button
                variant="outline"
                onClick={() => bloc.backToCompose()}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => bloc.confirm()}
                disabled={phase.resolved.payableCount === 0}
                className="flex-1"
              >
                Pay {formatUsd(phase.resolved.totalAmountCents)}
              </Button>
            </>
          )}
          {phase.kind === "kicking_off" && (
            <Button disabled className="flex-1">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Starting…
            </Button>
          )}
          {phase.kind === "watching" &&
            (job?.status === "running" || job?.status === "pending") && (
              <Button
                variant="outline"
                onClick={() => bloc.cancel()}
                className="flex-1"
              >
                Cancel
              </Button>
            )}
          {phase.kind === "cancelling" && (
            <Button disabled variant="outline" className="flex-1">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Cancelling…
            </Button>
          )}
          {/* Offered only when no run is live. A resolve or kickoff failure has
              no job and is safely retryable; a dropped subscription or failed
              cancel does, and returning to the email form there would let a
              second payout be enqueued over merchants the first run may still
              be paying. Those recover by closing and reopening the sheet, which
              re-attaches through resumeIfRunning(). */}
          {phase.kind === "error" && !phase.job && (
            <Button
              variant="outline"
              onClick={() => bloc.backToCompose()}
              className="flex-1"
            >
              Back to emails
            </Button>
          )}
          {isTerminal && (
            <Button
              variant="outline"
              onClick={() => bloc.reset()}
              className="flex-1"
            >
              Start over
            </Button>
          )}
        </div>
      }
    >
      <p className="text-muted-foreground text-sm">
        Pays the $5 activation bonus to merchants supplied by email. Each
        merchant is checked against both the lifecycle record and Stripe before
        anything is sent, so a bonus already paid — including one issued by hand
        in the Stripe dashboard — is skipped rather than paid twice.
      </p>

      {(phase.kind === "composing" || phase.kind === "resolving") && (
        <div className="mt-4">
          <label
            htmlFor="backfill-emails"
            className="text-muted-foreground text-xs font-medium"
          >
            Merchant emails
          </label>
          <textarea
            id="backfill-emails"
            value={emailsInput}
            onChange={(e) => bloc.setEmailsInput(e.target.value)}
            disabled={phase.kind === "resolving"}
            rows={10}
            placeholder={"one@example.com\ntwo@example.com"}
            className="border-border text-foreground disabled:bg-muted mt-1 w-full rounded-md border p-2 font-mono text-xs focus:border-blue-400 focus:outline-none"
          />
          <p className="text-subtle-foreground mt-1 text-xs">
            Newline, comma, or semicolon separated. Duplicates are collapsed.
          </p>

          <label className="mt-4 flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={reviveClosedTracks}
              onChange={(e) => bloc.setReviveClosedTracks(e.target.checked)}
              disabled={phase.kind === "resolving"}
              className="border-border mt-0.5 h-3.5 w-3.5 shrink-0 rounded"
            />
            <span className="text-muted-foreground text-xs">
              Also reopen expired and failed tracks
              <span className="text-subtle-foreground mt-0.5 block">
                Off by default. These merchants were closed out and no automatic
                path will pay them, so reopening one is a deliberate spending
                decision. Already-paid merchants are still skipped.
              </span>
            </span>
          </label>
        </div>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <div className="text-sm text-red-700">
            <p>{phase.message}</p>
            {phase.job && (
              <p className="mt-2 text-xs">
                Run <span className="font-mono">{phase.job.id}</span> is still
                going — this only lost track of it. Reopen the tool to pick it
                back up. Don&rsquo;t start a second run.
              </p>
            )}
          </div>
        </div>
      )}

      {(phase.kind === "resolved" || phase.kind === "kicking_off") && (
        <ResolvedSummary resolved={phase.resolved} />
      )}

      {job && (
        <div className="border-border mt-6 space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <JobStatusBadge status={job.status} />
            <span className="text-subtle-foreground text-xs">
              {job.processed} of {job.total ?? "?"} processed
            </span>
          </div>

          <CounterGrid raw={job.counters} />

          {job.status === "failed" && job.error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-xs font-medium text-red-700">Error</p>
              <pre className="mt-1 overflow-x-auto text-xs text-red-600">
                {job.error}
              </pre>
            </div>
          )}

          <p className="text-subtle-foreground text-xs">Job ID: {job.id}</p>
        </div>
      )}
    </ToolSheetShell>
  );
}
